/**
 * @component SignupPage
 * @description
 * The SignupPage guides new users through a four-step registration and purchase flow
 * for booking the software license and creating a new account.
 * It implements all required validation, progress tracking, and user feedback via Chakra UI.
 *
 * ---
 * **Flow Overview:**
 * 1️⃣ **Rechnung (Invoice)** — Displays pricing details and product description.
 *    - Clicking "Weiter" proceeds to Step 2.
 *    - Clicking "Zurück" redirects to the Login screen.
 *
 * 2️⃣ **Konto erstellen (Account creation)** — User fills out personal and billing data.
 *    - Required fields: Anrede, Vorname, Nachname, E-Mail, Passwort, Straße, Hausnummer, Postleitzahl, Ort, Land.
 *    - “Firma” is optional.
 *    - Validation errors show inline (e.g., invalid email or empty fields).
 *    - “Weiter” stays disabled until all required fields are valid.
 *    - On submission, the API validates the email address.
 *      - If already used → error toast shown.
 *      - If valid → advances to Step 3.
 *    - Displays a loading state while waiting for backend response.
 *
 * 3️⃣ **Zusammenfassung (Summary)** — Displays entered data in a table for confirmation.
 *    - Empty optional values (e.g., Firma) are replaced by an mdash (—).
 *    - Three checkboxes confirm user acceptance of AGB, SaaS terms, and Privacy Policy.
 *    - Each checkbox is required and marked with a red asterisk (*).
 *    - Clicking the blue links opens the corresponding PDF documents.
 *    - “Weiter” remains disabled until all three checkboxes are checked.
 *
 * 4️⃣ **Kaufbestätigung (Purchase confirmation)** — Displays a confirmation message.
 *    - Informs the user that a confirmation email was sent.
 *    - Displays the email address and link expiration time (15 minutes).
 *    - Provides a “E-Mail erneut zusenden” button with cooldown logic (60 seconds)
 *      to resend the confirmation email.
 *
 * ---
 * **UI Features:**
 * - Fully responsive (>300px)
 * - ProgressBar updates dynamically:
 *   - ✅ Green checkmark for completed steps
 *   - 🔵 Highlight for current step
 *   - ⚪ Default gray for pending steps
 * - Uses Chakra UI components for layout, forms, and interactivity.
 * - Uses `useSignupForm` hook for form state and validation logic.
 * - Uses `toaster` for feedback messages (success/error).
 *
 * ---
 * **Technical Notes:**
 * - The `useSignupForm` hook handles API submission (`signupApi`).
 * - Local storage temporarily stores `signupEmail` for later verification.
 * - Cooldown timer implemented with `useEffect` for resend button delay.
 * - Step transitions managed via local `useState` for clarity.
 *
 * @returns {JSX.Element} Multi-step signup page with full account creation and confirmation flow.
 */
import React from 'react'
import { signupApi, resendSignupEmailApi } from '@/api/auth.api'
import { SignupBackButton } from '@/components/auth/SignupBackButton'
import { PasswordInput } from '@/components/ui/password-input'
import { InfoBanner } from '@/components/ui//InfoBanner'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import {
  Box,
  Button,
  Checkbox,
  createListCollection,
  Field,
  Flex,
  HStack,
  Icon,
  Input,
  Link,
  Select,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SignupSchema, INITIAL_SIGNUP_VALUES, type SignupData } from '@/common/auth.common'
import { SALES_REGION_OPTIONS, parseSalesRegions, joinSalesRegions } from '@/common/salesRegion'
import { useFormik } from 'formik'
import SignupProgressBar from '@/components/auth/SignupProgressBar'
import { LuCheck, LuMonitorSmartphone, LuX } from 'react-icons/lu'
import { endUserSession } from '@/providers/auth/session'
import { mapAuthError } from '@/helper/apiError.helpers'

const SIMPLE_FIELDS: { name: keyof SignupData; label: string }[] = [
  { name: 'company', label: 'Firma' },
  { name: 'street', label: 'Adresse' },
  {
    name: 'streetNumber',
    label: 'Hausnummer',
  },
  {
    name: 'postal',
    label: 'Postleitzahl',
  },
]

export default function SignupPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1)
  const [cooldown, setCooldown] = useState(0)
  const [resendError, setResendError] = useState('')
  const [agb, setAgb] = useState(false)
  const [terms, setTerms] = useState(false)
  const [privacy, setPrivacy] = useState(false)
  const [avContract, setAvContract] = useState(false)
  const minutes = 15

  const formik = useFormik<SignupData>({
    initialValues: INITIAL_SIGNUP_VALUES,
    validationSchema: SignupSchema,
    onSubmit: async values => {
      try {
        await signupApi({
          email: values.email,
          password: values.password,
          firstname: values.firstname,
          lastname: values.lastname,
          postal: values.postal,
          city: values.city,
          street: values.street,
          streetNumber: values.streetNumber,
          company: values.company || '',
          workingArea: values.workingArea,
          workRadius: values.workRadius,
          salesRegion: values.salesRegion,
          topics: values.topics,
        })

        localStorage.setItem('signupEmail', values.email)
        setStep(4)
        setCooldown(60)
        toast.success('Registrierung erfolgreich, bitte E-Mail bestätigen.')
      } catch (e: any) {
        const uiError = mapAuthError(e)
        toast.error(uiError.title || 'Registrierung fehlgeschlagen', uiError.message)
      }
    },
  })

  // Countdown effect for resend cooldown
  useEffect(() => {
    if (cooldown <= 0) return // stop when countdown is done
    const timer = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0 // reset to 0 when done
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer) // cleanup
  }, [cooldown])

  const canContinueStep2 = useMemo(() => {
    const required: Array<keyof typeof formik.values> = [
      'salutation',
      'firstname',
      'lastname',
      'email',
      'password',
      'street',
      'postal',
      'city',
    ]

    const allFilled = required.every(k => Boolean(formik.values[k]))
    // isValid is accurate after fields are touched or validation has run at least once
    // We also ensure at least one required field is touched to avoid enabling on pristine forms.
    const hasAnyTouched = required.some(k => formik.touched[k])

    return allFilled && hasAnyTouched && formik.isValid && !formik.isSubmitting
  }, [formik.values, formik.touched, formik.isValid, formik.isSubmitting])

  const resendEmail = async () => {
    try {
      await resendSignupEmailApi(formik.values.email)
      toast.success('E-Mail erneut gesendet')
      setCooldown(60)
      setResendError('')
    } catch (e: any) {
      setResendError(e?.message || 'E-Mail konnte nicht gesendet werden.')
    }
  }
  const salutationCollection = createListCollection({
    items: [
      { label: 'Herr', value: 'Herr' },
      { label: 'Frau', value: 'Frau' },
      { label: 'Andere', value: 'Andere' },
    ],
  })
  const countryCollection = createListCollection({
    items: [
      { label: 'Deutschland', value: 'Deutschland' },
      { label: 'Schweiz', value: 'Schweiz' },
      { label: 'Österreich', value: 'Österreich' },
    ],
  })

  const topicCollection = createListCollection({
    items: [
      { value: 'RETIREMENT_PROVISION', label: 'Altersvorsorge' },
      { value: 'VERMOEGEN_PLUS', label: 'VermögenPlus' },
      { value: 'MEIN_INVEST', label: 'MeinInvest' },
      { value: 'UNION_DEPOT_KOMFORT', label: 'UnionDepot Komfort' },
      { value: 'WERT_ANLAGE', label: 'WertAnlage' },
      { value: 'ELTIF', label: 'Eltif' },
      { value: 'WEALTH_STRUCTURING', label: 'Vermögensstrukturierung' },
      { value: 'CUSTOMER_SERVICE_CENTER', label: 'Begleitung im Kundenservicecenter' },
      { value: 'PREMIUM_SEGMENT', label: 'Begleitung im gehobenen Segment' },
      { value: 'BANKING_WORKSPACE', label: 'Banking Workspace' },
      { value: 'FUND_LINKED_PENSION', label: 'Fondsgebundene Rentenversicherung' },
    ],
  })

  const salesRegionCollection = createListCollection({ items: SALES_REGION_OPTIONS })

  const areaCollection = useMemo(
    () =>
      createListCollection({
        items: [
          { value: 'CITY_RADIUS', label: 'Stadt + Radius (km)' },
          { value: 'FEDERAL_STATE', label: 'Bundesland' },
          { value: 'GERMANY', label: 'Deutschland' },
          { value: 'AUSTRIA', label: 'Österreich' },
          { value: 'SWITZERLAND', label: 'Schweiz' },
        ],
      }),
    []
  )

  const logout = async () => {
    // Central teardown (SIB-924): server logout + full client session reset,
    // including the user store. Local cleanup happens even if the server call fails.
    await endUserSession()
    navigate('/login', { replace: true })
  }

  return (
    <Box minH="100vh" bg="bg.canvas" pb={{ base: 8, md: 10 }}>
      {step === 5 ? (
        <SignupBackButton label="Abmelden" onBack={logout} />
      ) : (
        <SignupBackButton onBack={() => navigate('/login')} />
      )}

      {step !== 5 && (
        <VStack
          gap={{ base: 3, md: 8 }}
          mt={{ base: 8, md: 28 }}
          align="center"
          textAlign="center"
        >
          <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="semibold" color="fg.muted">
            Softwarelizenz erwerben
          </Text>
          <SignupProgressBar current={(step > 4 ? 4 : step) as 1 | 2 | 3 | 4} />
        </VStack>
      )}

      <Box w="100%" maxW="3xl" mx="auto" px={{ base: 4, md: 0 }}>
        {/* STEP 1 — RECHNUNG */}
        {step === 1 && (
          <Box mt={{ base: 6, md: 14 }}>
            <Box
              bg="bg"
              borderRadius="16px"
              boxShadow="0px 0px 1px rgba(24,24,27,0.3), 0px 4px 8px rgba(24,24,27,0.1)"
              p={{ base: 3, md: 4 }}
              w="full"
              maxW="1024px"
              mx="auto"
            >
              <Box
                border="1px solid"
                borderColor="border.emphasized"
                borderRadius="8px"
                overflow="hidden"
                w="full"
                display="grid"
                gridTemplateColumns="2fr 1fr"
              >
                {/* Header row */}
                <Box
                  p="12px"
                  borderRight="1px solid"
                  borderRightColor="border.emphasized"
                  borderBottom="1px solid"
                  borderBottomColor="border.emphasized"
                >
                  <Text fontSize="14px" fontWeight="600">
                    Beschreibung
                  </Text>
                </Box>
                <Box
                  p="12px"
                  borderBottom="1px solid"
                  borderBottomColor="border.emphasized"
                  display="flex"
                  alignItems="center"
                >
                  <Text fontSize="14px" fontWeight="600">
                    Preis
                  </Text>
                </Box>

                {/* Row 1 */}
                <Box
                  p="12px"
                  borderRight="1px solid"
                  borderRightColor="border.emphasized"
                  borderBottom="1px solid"
                  borderBottomColor="border.emphasized"
                >
                  <Text fontSize="14px">
                    Bürosoftware für selbstständige Unternehmensberater und Coaches (Lizenz für 12
                    Monate nach Freischaltung)
                  </Text>
                </Box>
                <Box
                  p="12px"
                  borderBottom="1px solid"
                  borderBottomColor="border.emphasized"
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <Text fontSize="14px">149,00 €</Text>
                </Box>

                {/* Row 2 */}
                <Box
                  p="12px"
                  borderRight="1px solid"
                  borderRightColor="border.emphasized"
                  borderBottom="1px solid"
                  borderBottomColor="border.emphasized"
                >
                  <Text fontSize="14px">zzgl. 19 % MwSt.</Text>
                </Box>
                <Box
                  p="12px"
                  borderBottom="1px solid"
                  borderBottomColor="border.emphasized"
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <Text fontSize="14px">28,31 €</Text>
                </Box>

                {/* Row 3 */}
                <Box p="12px" borderRight="1px solid" borderRightColor="border.emphasized">
                  <Text fontSize="14px" fontWeight="700">
                    Gesamtpreis
                  </Text>
                </Box>
                <Box p="12px" display="flex" alignItems="center" justifyContent="flex-end">
                  <Text fontSize="14px" fontWeight="700">
                    177,31 €
                  </Text>
                </Box>
              </Box>
              <InfoBanner />
            </Box>
            <Box maxW="736px" mx="auto" px={{ base: 0, md: 0 }}>
              <HStack justify="flex-end" gap={3} mt={6} px={{ base: 2, md: 0 }}>
                <Button variant="outline" onClick={() => navigate('/login')}>
                  Zurück
                </Button>
                <Button onClick={() => setStep(2)}>Weiter</Button>
              </HStack>
            </Box>
          </Box>
        )}

        {/* STEP 2 — KONTO ERSTELLEN */}
        {step === 2 && (
          <Box mt={{ base: 6, md: 14 }}>
            <Box
              bg="bg"
              borderRadius="16px"
              boxShadow="0px 0px 1px rgba(24,24,27,0.3), 0px 4px 8px rgba(24,24,27,0.1)"
              p={{ base: 4, md: 6 }}
              w="full"
              maxW="1024px"
              mx="auto"
            >
              {/* Form grid */}
              <VStack align="stretch" gap={6}>
                <Text fontSize="sm" fontWeight="semibold" color="fg.muted">
                  Kontodaten &amp; Rechnungsadresse
                </Text>
                {/* Row 1 — Anrede, Vorname, Nachname */}
                <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
                  <Box>
                    <Field.Root required>
                      <Field.Label mt={0.5} borderRadius="16px" color="fg">
                        Anrede
                        <Text as="span" color="red.fg">
                          *
                        </Text>
                      </Field.Label>
                      <Select.Root
                        name="salutation"
                        collection={salutationCollection}
                        value={formik.values.salutation ? [formik.values.salutation] : []}
                        onValueChange={details => {
                          const nextValue = details.value[0] ?? ''
                          formik.setFieldValue('salutation', nextValue)
                        }}
                      >
                        <Select.Control>
                          <Select.Trigger>
                            <Select.ValueText placeholder="Bitte auswählen" />
                            <Select.IndicatorGroup>
                              <Select.Indicator /> {/* ▼ arrow */}
                              <Select.ClearTrigger /> {/* ✖ optional clear */}
                            </Select.IndicatorGroup>
                          </Select.Trigger>
                        </Select.Control>

                        <Select.Positioner>
                          <Select.Content>
                            {salutationCollection.items.map(opt => (
                              <Select.Item key={opt.value} item={opt.value}>
                                {opt.label}
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Positioner>
                      </Select.Root>

                      {formik.touched.salutation && formik.errors.salutation && (
                        <Field.ErrorText>{formik.errors.salutation}</Field.ErrorText>
                      )}
                    </Field.Root>
                  </Box>

                  <Box>
                    <Text as="label" fontWeight="medium" fontSize="14px" color="fg">
                      Vorname{' '}
                      <Text as="span" color="red.fg">
                        *
                      </Text>
                    </Text>
                    <Input
                      type="text"
                      name="firstname"
                      value={formik.values.firstname}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      border="1px solid"
                      borderColor="border"
                      borderRadius="sm"
                      mt={1}
                      fontSize="14px"
                    />
                    {formik.touched.firstname && formik.errors.firstname && (
                      <Text color="red.fg" fontSize="sm" mt={1}>
                        {formik.errors.firstname}
                      </Text>
                    )}
                  </Box>

                  <Box>
                    <Text as="label" fontWeight="medium" fontSize="14px">
                      Nachname{' '}
                      <Text as="span" color="red.fg">
                        *
                      </Text>
                    </Text>
                    <Input
                      type="text"
                      name="lastname"
                      value={formik.values.lastname}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      border="1px solid"
                      borderColor="border"
                      borderRadius="sm"
                      mt={1}
                      fontSize="14px"
                    />
                    {formik.touched.lastname && formik.errors.lastname && (
                      <Text color="red.fg" fontSize="sm" mt={1}>
                        {formik.errors.lastname}
                      </Text>
                    )}
                  </Box>
                </SimpleGrid>

                {/* Row 2 — Firma, E-Mail, Passwort */}
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} alignItems="start">
                  {/* E-Mail field */}
                  <Box>
                    <Text as="label" fontWeight="medium" fontSize="14px">
                      E-Mail{' '}
                      <Text as="span" color="red.fg">
                        *
                      </Text>
                    </Text>
                    <Input
                      type="email"
                      name="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      border="1px solid"
                      borderColor="border"
                      borderRadius="sm"
                      mt={1}
                      fontSize="14px"
                    />
                    {formik.touched.email && formik.errors.email && (
                      <Text color="red.fg" fontSize="sm" mt={1}>
                        {formik.errors.email}
                      </Text>
                    )}
                  </Box>

                  {/* Passwort field */}
                  <Box>
                    <Field.Root
                      required
                      mb={2}
                      mt={0.5}
                      invalid={!!(formik.touched.password && formik.errors.password)}
                    >
                      <Field.Label>
                        Passwort{' '}
                        <Text as="span" color="red.fg">
                          *
                        </Text>
                      </Field.Label>
                      <PasswordInput
                        autoComplete="new-password"
                        {...formik.getFieldProps('password')}
                        onFocus={() => formik.setFieldTouched('password', true)}
                        onChange={e => {
                          formik.handleChange(e)
                          formik.setStatus(null) // reset global error
                        }}
                      />

                      {formik.touched.password && formik.errors.password && (
                        <Field.ErrorText>{formik.errors.password}</Field.ErrorText>
                      )}
                    </Field.Root>
                  </Box>
                </SimpleGrid>

                {/* Row 3 — Adresse, Hausnummer */}
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                  {SIMPLE_FIELDS.map(({ name, label }) => (
                    <Box key={name}>
                      {/* Label — Firma has no red asterisk */}
                      <Text as="label" fontWeight="medium" fontSize="14px">
                        {label}
                        {name !== 'company' && (
                          <Text as="span" color="red.fg">
                            *
                          </Text>
                        )}
                      </Text>

                      {/* Input field */}
                      <Input
                        type="text"
                        name={name}
                        value={(formik.values as any)[name]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        border="1px solid"
                        borderColor="border"
                        borderRadius="sm"
                        mt={1}
                        fontSize="14px"
                      />

                      {/* Inline validation error */}
                      {formik.touched[name] && formik.errors[name] && (
                        <Text color="red.fg" fontSize="sm" mt={1}>
                          {formik.errors[name]}
                        </Text>
                      )}
                    </Box>
                  ))}
                </SimpleGrid>

                {/* Row 4 — Postleitzahl, Ort, Land */}
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                  <Box>
                    <Text as="label" fontWeight="medium" fontSize="14px">
                      Ort{' '}
                      <Text as="span" color="red.fg">
                        *
                      </Text>
                    </Text>
                    <Input
                      name="city"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      border="1px solid"
                      borderColor="border"
                      borderRadius="sm"
                      mt={1}
                      fontSize="14px"
                    />
                    {formik.touched.city && formik.errors.city && (
                      <Text color="red.fg" fontSize="sm" mt={1}>
                        {formik.errors.city}
                      </Text>
                    )}
                  </Box>

                  <Box>
                    <Field.Root required>
                      <Field.Label mt={0.5}>
                        Land
                        <Text as="span" color="red.fg">
                          *
                        </Text>
                      </Field.Label>

                      <Select.Root
                        name="country"
                        collection={countryCollection}
                        value={formik.values.country ? [formik.values.country] : []}
                        onValueChange={details => {
                          const nextValue = details.value[0] ?? ''
                          formik.setFieldValue('country', nextValue)
                        }}
                      >
                        <Select.Control>
                          <Select.Trigger>
                            <Select.ValueText placeholder="Bitte auswählen" />
                            <Select.IndicatorGroup>
                              <Select.Indicator />
                              <Select.ClearTrigger />
                            </Select.IndicatorGroup>
                          </Select.Trigger>
                        </Select.Control>

                        <Select.Positioner>
                          <Select.Content>
                            {countryCollection.items.map(opt => (
                              <Select.Item key={opt.value} item={opt.value}>
                                {opt.label}
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Positioner>
                      </Select.Root>

                      {formik.touched.country && formik.errors.country && (
                        <Field.ErrorText>{formik.errors.country}</Field.ErrorText>
                      )}
                    </Field.Root>
                  </Box>
                </SimpleGrid>

                <Text fontSize="lg" mt={6} mb={4}>
                  Wirkungsradius
                </Text>

                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                  <Box>
                    <Text as="label" fontWeight="semibold" fontSize="sm">
                      Bereich
                    </Text>

                    <Select.Root
                      collection={areaCollection}
                      value={formik.values.workingArea ? [formik.values.workingArea] : []}
                      onValueChange={({ value }) =>
                        formik.setFieldValue('workingArea', value[0] ?? '')
                      }
                    >
                      <Select.Control mt={1}>
                        <Select.Trigger>
                          <Select.ValueText placeholder="Bitte auswählen" />
                        </Select.Trigger>
                        <Select.IndicatorGroup>
                          <Select.Indicator />
                        </Select.IndicatorGroup>
                      </Select.Control>

                      <Select.Positioner>
                        <Select.Content>
                          {areaCollection.items.map(item => (
                            <Select.Item key={item.value} item={item.value}>
                              {item.label}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Positioner>
                    </Select.Root>
                  </Box>

                  <Box>
                    <Text as="label" fontWeight="semibold" fontSize="sm">
                      Radius (km)
                    </Text>
                    <Input
                      name="workRadius"
                      value={formik.values.workRadius}
                      onChange={e => {
                        const sanitized = e.target.value.replace(/\D/g, '')
                        formik.setFieldValue('workRadius', sanitized)
                      }}
                      onBlur={formik.handleBlur}
                      mt={1}
                    />
                  </Box>

                  <Box>
                    <Text as="label" fontWeight="semibold" fontSize="sm">
                      Vertriebsregion
                    </Text>
                    <Select.Root
                      name="salesRegion"
                      collection={salesRegionCollection}
                      value={parseSalesRegions(formik.values.salesRegion)}
                      multiple
                      mt={1}
                      onValueChange={details => {
                        formik.setFieldValue('salesRegion', joinSalesRegions(details.value))
                      }}
                    >
                      <Select.Control>
                        <Select.Trigger minH="40px">
                          <HStack gap={2} wrap="wrap" flex="1">
                            {parseSalesRegions(formik.values.salesRegion).length > 0 ? (
                              salesRegionCollection.items
                                .filter(item =>
                                  parseSalesRegions(formik.values.salesRegion).includes(item.value)
                                )
                                .map(item => (
                                  <Box
                                    key={item.value}
                                    px={2}
                                    py={0.5}
                                    borderRadius="sm"
                                    bg="bg.muted"
                                    fontSize="xs"
                                  >
                                    {item.label}
                                  </Box>
                                ))
                            ) : (
                              <Text color="fg.muted">Bitte auswählen</Text>
                            )}
                          </HStack>

                          <Select.IndicatorGroup>
                            <Select.Indicator />
                          </Select.IndicatorGroup>
                        </Select.Trigger>
                      </Select.Control>

                      <Select.Positioner>
                        <Select.Content maxH="260px" overflowY="auto">
                          {salesRegionCollection.items.map(opt => {
                            const selected = parseSalesRegions(formik.values.salesRegion).includes(
                              opt.value
                            )
                            return (
                              <Select.Item key={opt.value} item={opt.value}>
                                <HStack justify="space-between" w="full">
                                  <Text>{opt.label}</Text>
                                  {selected && <Text>✓</Text>}
                                </HStack>
                              </Select.Item>
                            )
                          })}
                        </Select.Content>
                      </Select.Positioner>
                    </Select.Root>
                    <Text fontSize="xs" color="fg.muted" mt={1}>
                      Sie können mehrere Vertriebsregionen auswählen.
                    </Text>
                  </Box>
                </SimpleGrid>

                <Box>
                  <Text fontSize="lg" mt={8} mb={4}>
                    Themen von Interesse
                  </Text>

                  <Text fontSize="sm" color="fg.muted" mb={4}>
                    Wählen Sie die Themen aus, die für Sie relevant sind. Sie können mehrere Themen
                    auswählen. Wenn Sie keine Auswahl treffen, werden alle Themen berücksichtigt.
                  </Text>

                  <Select.Root
                    name="topics"
                    collection={topicCollection}
                    value={formik.values.topics ?? []}
                    multiple
                    onValueChange={details => {
                      formik.setFieldValue('topics', details.value)
                    }}
                  >
                    <Select.Control>
                      <Select.Trigger minH="40px">
                        <HStack gap={2} wrap="wrap" flex="1">
                          <Text color="fg.muted">Bitte Themen auswählen</Text>
                        </HStack>

                        <Select.IndicatorGroup>
                          <Select.Indicator />
                        </Select.IndicatorGroup>
                      </Select.Trigger>
                    </Select.Control>

                    <Select.Positioner>
                      <Select.Content maxH="260px" overflowY="auto">
                        {topicCollection.items.map(opt => {
                          const selected = (formik.values.topics ?? []).includes(opt.value)

                          return (
                            <Select.Item key={opt.value} item={opt.value}>
                              <HStack justify="space-between" w="full">
                                <Text>{opt.label}</Text>
                                {selected && <Text>✓</Text>}
                              </HStack>
                            </Select.Item>
                          )
                        })}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>

                  {(formik.values.topics ?? []).length > 0 && (
                    <Box mt={3}>
                      <Text fontSize="sm" fontWeight="medium" color="fg.muted" mb={2}>
                        {(formik.values.topics ?? []).length}{' '}
                        {(formik.values.topics ?? []).length === 1 ? 'Thema' : 'Themen'} ausgewählt
                      </Text>
                      <Flex wrap="wrap" gap={2}>
                        {(formik.values.topics ?? []).map(code => {
                          const label =
                            topicCollection.items.find(i => i.value === code)?.label ?? code
                          return (
                            <HStack
                              key={code}
                              gap={2}
                              px={3}
                              py={1}
                              borderWidth="1px"
                              borderColor="border.subtle"
                              borderRadius="md"
                              bg="bg.subtle"
                            >
                              <Box as={LuCheck} color="green.fg" boxSize="14px" />
                              <Text fontSize="sm">{label}</Text>
                              <Box
                                as="button"
                                type="button"
                                aria-label={`${label} entfernen`}
                                onClick={() =>
                                  formik.setFieldValue(
                                    'topics',
                                    (formik.values.topics ?? []).filter(c => c !== code)
                                  )
                                }
                                display="inline-flex"
                                color="fg.muted"
                                _hover={{ color: 'fg' }}
                                cursor="pointer"
                              >
                                <LuX />
                              </Box>
                            </HStack>
                          )
                        })}
                      </Flex>
                    </Box>
                  )}
                </Box>

                <Text fontSize="sm" color="fg.muted" mt={2}>
                  Pflichtfelder sind mit{' '}
                  <Text as="span" color="red.fg">
                    *
                  </Text>{' '}
                  markiert.
                </Text>
              </VStack>
            </Box>
            {/* Buttons */}
            <Box maxW="736px" mx="auto" px={{ base: 0, md: 0 }}>
              <HStack justify="flex-end" gap={3} mt={6} px={{ base: 2, md: 0 }}>
                <Button
                  variant="outline"
                  border="1px solid"
                  borderColor="gray.muted"
                  color="gray.fg"
                  onClick={() => setStep(1)}
                >
                  Zurück
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!canContinueStep2}
                  bg="gray.solid"
                  color="gray.contrast"
                >
                  Weiter
                </Button>
              </HStack>
            </Box>
          </Box>
        )}

        {/* STEP 3 — ZUSAMMENFASSUNG */}
        {step === 3 && (
          <Box mt={{ base: 6, md: 14 }}>
            <Box
              bg="bg"
              borderRadius="16px"
              boxShadow="0px 0px 1px rgba(24,24,27,0.3), 0px 4px 8px rgba(24,24,27,0.1)"
              p={{ base: 4, md: 6 }}
              w="full"
              maxW="1024px"
              mx="auto"
            >
              {/* Grid table layout */}
              <Box
                display="grid"
                gridTemplateColumns="minmax(0, 1fr) minmax(0, 1fr)" // ⬅ always 2 columns
                border="1px solid"
                borderColor="border"
                borderRadius="8px"
                overflow="hidden"
                mb={8}
              >
                {[
                  {
                    label: 'Anschrift',
                    value: `${formik.values.salutation} ${formik.values.firstname} ${formik.values.lastname}`,
                  },
                  {
                    label: 'Wohnort',
                    value: `${formik.values.street} ${formik.values.streetNumber}, ${formik.values.postal} ${formik.values.city}`,
                  },
                  {
                    label: 'Firma',
                    value: formik.values.company || '—',
                  },
                  {
                    label: 'E-Mail-Adresse',
                    value: formik.values.email,
                  },
                  { label: 'Preis', value: '177,31 €' },
                ].map(({ label, value }, idx, arr) => (
                  <React.Fragment key={label}>
                    <Box
                      p="12px"
                      borderBottomWidth={idx < arr.length - 1 ? '1px' : 0}
                      borderBottomColor={idx < arr.length - 1 ? 'border' : 'transparent'}
                      borderRightWidth="1px" // ⬅ keep column divider on mobile too
                      borderRightColor="border"
                      display="flex"
                      alignItems="center"
                    >
                      <Text fontSize="14px" color="fg.default" lineHeight="20px">
                        {label}
                      </Text>
                    </Box>
                    <Box
                      p="12px"
                      borderBottomWidth={idx < arr.length - 1 ? '1px' : 0}
                      borderBottomColor={idx < arr.length - 1 ? 'border' : 'transparent'}
                      display="flex"
                      alignItems="center"
                    >
                      <Text fontSize="14px" fontWeight="600" color="fg" lineHeight="20px">
                        {value}
                      </Text>
                    </Box>
                  </React.Fragment>
                ))}
              </Box>

              {/* Checkbox section with links */}
              <VStack align="start" gap={5}>
                {[
                  {
                    id: 'agb',
                    label: 'Ich stimme den allgemeinen Geschäftsbedingungen zu.',
                    link: 'Allgemeine Geschäftsbedingungen öffnen',
                    href: '/docs/Allgemeine Geschäftsbedingungen (AGB).pdf',
                    value: agb,
                    setter: setAgb,
                  },
                  {
                    id: 'terms',
                    label: 'Ich stimme den Vertragsbedingungen zu.',
                    link: 'Vertragsbedingungen zur Nutzung von Software as a Service öffnen',
                    href: '/docs/SaaS Vertrag.pdf',
                    value: terms,
                    setter: setTerms,
                  },
                  {
                    id: 'privacy',
                    label: 'Ich stimme der Datenschutzerklärung zu.',
                    link: 'Datenschutzerklärung öffnen',
                    href: '/docs/Datenschutz.pdf',
                    value: privacy,
                    setter: setPrivacy,
                  },
                  {
                    id: 'avContract',
                    label: 'Ich stimme der Auftragsverarbeitungsvereinbarung zu.',
                    link: 'Auftragsverarbeitungsvereinbarung öffnen',
                    href: '/docs/Auftragsverarbeitungsvertrag (AV Vertrag).pdf',
                    value: avContract,
                    setter: setAvContract,
                  },
                ].map(({ id, label, link, href, value, setter }) => (
                  <Box key={id}>
                    <HStack align="center" gap={3}>
                      <Checkbox.Root
                        checked={value}
                        onCheckedChange={details => setter(Boolean(details.checked))}
                      >
                        <Checkbox.HiddenInput />
                        <HStack gap={3} align="center">
                          <Checkbox.Control>
                            <Checkbox.Indicator />
                          </Checkbox.Control>
                          <Checkbox.Label>
                            <Text fontSize="14px" fontWeight="600" color="fg" lineHeight="20px">
                              {label}
                              <Text as="span" color="red.fg">
                                *
                              </Text>
                            </Text>
                          </Checkbox.Label>
                        </HStack>
                      </Checkbox.Root>
                    </HStack>

                    {/* Blue link below */}
                    <Link
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      fontSize="14px"
                      color="fg.info"
                      lineHeight="20px"
                      mt={1}
                      display="inline-block"
                    >
                      {link}
                    </Link>
                  </Box>
                ))}
              </VStack>
            </Box>
            {/* Buttons */}
            <Box maxW="736px" mx="auto" px={{ base: 0, md: 0 }}>
              <HStack justify="flex-end" gap={3} mt={6} px={{ base: 2, md: 0 }}>
                <Button variant="outline" onClick={() => setStep(2)}>
                  Zurück
                </Button>
                <Button
                  onClick={() => formik.handleSubmit()}
                  loading={formik.isSubmitting}
                  loadingText="Wird gesendet..."
                  disabled={!agb || !terms || !privacy || !avContract || formik.isSubmitting}
                >
                  Weiter
                </Button>
              </HStack>
            </Box>
          </Box>
        )}

        {/* STEP 4 — KAUFBESTÄTIGUNG */}
        {step === 4 && (
          <Box mt={{ base: 6, md: 14 }}>
            <Box
              bg="bg"
              borderRadius="16px"
              boxShadow="0px 0px 1px rgba(24,24,27,0.3), 0px 4px 8px rgba(24,24,27,0.1)"
              p={{ base: 4, md: 6 }}
              w="full"
              maxW="1024px"
              mx="auto"
            >
              <VStack align="start" gap={4}>
                {/* Title */}
                <Text fontSize="18px" lineHeight="28px" fontWeight="600" color="fg">
                  Registrierung abschließen
                </Text>

                {/* Description */}
                <Text fontSize="18px" lineHeight="28px" fontWeight="400" color="fg">
                  Wir haben Ihnen eine E-Mail an{' '}
                  <Text as="span" color="fg.info" fontWeight="600">
                    {formik.values.email}
                  </Text>{' '}
                  gesendet. Bitte klicken Sie auf den darin enthaltenen Link, um Ihre Registrierung
                  abzuschließen. Der Link ist{' '}
                  <Text as="span" fontWeight="600">
                    {minutes}
                  </Text>{' '}
                  Minuten gültig.
                </Text>

                {/* Resend button */}
                <Button variant="plain" onClick={resendEmail} disabled={cooldown > 0} size="md">
                  E-Mail erneut zusenden
                </Button>

                {/* Cooldown info */}
                {cooldown > 0 && (
                  <Box
                    border="1px solid var(--chakra-colors-border-subtle)"
                    borderRadius="lg"
                    p={3}
                  >
                    <Text fontSize="14px">In {cooldown} Sekunden erneut anfordern</Text>
                  </Box>
                )}

                {/* Error message */}
                {resendError && (
                  <Box
                    border="1px solid var(--chakra-colors-red-300)"
                    bg="red.50"
                    p={3}
                    borderRadius="lg"
                  >
                    <Text color="fg.error">{resendError}</Text>
                  </Box>
                )}
              </VStack>
            </Box>
            <Box maxW="768px" mx="auto">
              <HStack justify="flex-end" gap={4} mt={8}>
                <Button variant="outline" onClick={() => setStep(3)}>
                  Zurück
                </Button>
                <Button onClick={() => navigate('/login', { replace: true })}>Weiter</Button>
              </HStack>
            </Box>
          </Box>
        )}

        {/* STEP 5 — NOT SUPPORTED SCREEN */}
        {step === 5 && (
          <Box mt={{ base: 10, md: 20 }}>
            <Box
              bg="bg"
              borderRadius="16px"
              boxShadow="0px 0px 1px rgba(24,24,27,0.3), 0px 4px 8px rgba(24,24,27,0.1)"
              p={{ base: 8, md: 10 }}
              w="full"
              maxW="768px"
              mx="auto"
            >
              <VStack gap={6} align="center" textAlign="center">
                <Icon as={LuMonitorSmartphone} boxSize={10} color="fg.subtle" />

                <Text fontSize="20px" lineHeight="28px" fontWeight="600" color="fg">
                  Derzeit nicht auf Mobilgeräten
                </Text>

                <Text fontSize="14px" lineHeight="22px" color="fg.muted" maxW="480px">
                  Derzeit ist die Software noch nicht für Mobilgeräte unterstützt. Wir arbeiten
                  bereits mit Hochdruck daran, Ihnen diese Möglichkeit bald zur Verfügung zu
                  stellen. Zunächst fokussieren wir uns auf die meistgefragten Funktionen.
                </Text>
              </VStack>
            </Box>
          </Box>
        )}
      </Box>

      <Flex justify="center" mt={{ base: 6, md: 10 }} pb={4}>
        <Link
          href="https://www.polygran.de/impressum/"
          target="_blank"
          rel="noopener noreferrer"
          fontSize="13px"
          color="fg.muted"
        >
          Impressum
        </Link>
      </Flex>
    </Box>
  )
}
